import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { workflowService } from '../services/workflowService';

const WorkflowAutomation = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadTemplates();
  }, [user, userProfile, navigate]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await workflowService?.getWorkflowTemplates();
      setTemplates(data || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load workflow templates');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (templateId, currentStatus) => {
    try {
      await workflowService?.updateWorkflowTemplate(templateId, { isActive: !currentStatus });
      await loadTemplates();
    } catch (err) {
      alert('Failed to update template status');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this workflow template?')) return;
    
    try {
      await workflowService?.deleteWorkflowTemplate(templateId);
      await loadTemplates();
    } catch (err) {
      alert('Failed to delete template');
    }
  };

  const getTriggerTypeLabel = (type) => {
    const labels = {
      'consultation_completed': 'Consultation Completed',
      'status_change': 'Status Change',
      'time_based': 'Time-Based',
      'manual': 'Manual Trigger'
    };
    return labels?.[type] || type;
  };

  const getTriggerTypeColor = (type) => {
    const colors = {
      'consultation_completed': 'bg-green-100 text-green-800 border-green-200',
      'status_change': 'bg-[#E6F0F5] text-[#002B45] border-[#002B45]/20',
      'time_based': 'bg-purple-100 text-purple-800 border-purple-200',
      'manual': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading workflow templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Workflow Automation - Northstar Data Consulting</title>
      </Helmet>
      <Header />

      <main className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Workflow Automation</h1>
              <p className="text-muted-foreground">
                Configure automated post-consultation workflows and follow-up sequences
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Create Workflow
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Workflow Templates Grid */}
        {templates?.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Icon name="Workflow" size={64} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Workflow Templates</h3>
            <p className="text-muted-foreground mb-6">Create your first automated workflow to get started</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Create Workflow Template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {templates?.map((template) => (
              <div
                key={template?.id}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                {/* Template Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">{template?.name}</h3>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          template?.isActive
                            ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {template?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {template?.description && (
                      <p className="text-sm text-muted-foreground">{template?.description}</p>
                    )}
                  </div>
                </div>

                {/* Trigger Type */}
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">Trigger Type</p>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${getTriggerTypeColor(template?.triggerType)}`}>
                    <Icon name="Zap" size={14} />
                    {getTriggerTypeLabel(template?.triggerType)}
                  </span>
                </div>

                {/* Actions */}
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">Automated Actions</p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    {Array.isArray(template?.actions) && template?.actions?.length > 0 ? (
                      <ul className="space-y-1">
                        {template?.actions?.slice(0, 3)?.map((action, index) => (
                          <li key={index} className="text-xs text-foreground flex items-center gap-2">
                            <Icon name="CheckCircle2" size={12} className="text-primary" />
                            {action?.task_title || action?.action}
                          </li>
                        ))}
                        {template?.actions?.length > 3 && (
                          <li className="text-xs text-muted-foreground">
                            +{template?.actions?.length - 3} more actions
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-xs text-muted-foreground">No actions configured</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(template?.id, template?.isActive)}
                    iconName={template?.isActive ? 'PauseCircle' : 'PlayCircle'}
                    iconPosition="left"
                  >
                    {template?.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingTemplate(template)}
                    iconName="Edit"
                    iconPosition="left"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteTemplate(template?.id)}
                    iconName="Trash2"
                    iconPosition="left"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6">
            <Icon name="Zap" size={32} className="text-primary mb-3" />
            <h3 className="font-semibold text-foreground mb-2">Automated Triggers</h3>
            <p className="text-sm text-muted-foreground">
              Workflows automatically execute when consultations are completed or status changes occur
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6">
            <Icon name="CheckSquare" size={32} className="text-primary mb-3" />
            <h3 className="font-semibold text-foreground mb-2">Task Generation</h3>
            <p className="text-sm text-muted-foreground">
              Automatically create follow-up tasks with priorities and due dates for your team
            </p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
            <Icon name="TrendingUp" size={32} className="text-primary mb-3" />
            <h3 className="font-semibold text-foreground mb-2">Lead Scoring</h3>
            <p className="text-sm text-muted-foreground">
              Automatically calculate lead scores and update sales pipeline stages
            </p>
          </div>
        </div>
      </main>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <WorkflowTemplateModal
          template={editingTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          onSave={async () => {
            await loadTemplates();
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
};

// Workflow Template Modal Component
const WorkflowTemplateModal = ({ template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    triggerType: template?.triggerType || 'consultation_completed',
    isActive: template?.isActive !== undefined ? template?.isActive : true
  });
  const [saving, setSaving] = useState(false);

  const triggerTypeOptions = [
    { value: 'consultation_completed', label: 'Consultation Completed' },
    { value: 'status_change', label: 'Status Change' },
    { value: 'time_based', label: 'Time-Based' },
    { value: 'manual', label: 'Manual Trigger' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);

    try {
      const templateData = {
        name: formData?.name,
        description: formData?.description,
        triggerType: formData?.triggerType,
        isActive: formData?.isActive,
        actions: [
          { action: 'create_task', delay_hours: 24, task_title: 'Send follow-up email' },
          { action: 'create_task', delay_hours: 72, task_title: 'Schedule proposal meeting' },
          { action: 'update_pipeline', stage: 'proposal' }
        ]
      };

      if (template) {
        await workflowService?.updateWorkflowTemplate(template?.id, templateData);
      } else {
        await workflowService?.createWorkflowTemplate(templateData);
      }

      await onSave();
    } catch (error) {
      alert('Failed to save workflow template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-elevation-3 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            {template ? 'Edit Workflow Template' : 'Create Workflow Template'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Workflow Name"
            value={formData?.name}
            onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
            placeholder="e.g., Post-Consultation Follow-up"
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder="Describe what this workflow does..."
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <Select
            label="Trigger Type"
            value={formData?.triggerType}
            onChange={(value) => setFormData({ ...formData, triggerType: value })}
            options={triggerTypeOptions}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData?.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e?.target?.checked })}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-foreground">
              Activate workflow immediately
            </label>
          </div>

          <div className="bg-[#E6F0F5] border border-[#002B45]/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-[#002B45] mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[#002B45] mb-1">Default Actions</p>
                <p className="text-xs text-[#002B45]/80">
                  This workflow will automatically create follow-up tasks, calculate lead scores, and update the sales pipeline when triggered.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {template ? 'Update Workflow' : 'Create Workflow'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkflowAutomation;
